"use client"

import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import { useUserRole } from "@/providers/user-role-provider"

// The form behind /help. See app/api/contact/route.ts for the three spam
// layers; two of them live here — a honeypot input a human never sees, and
// the dwell time between mount and submit.
//
// Turnstile renders only when NEXT_PUBLIC_TURNSTILE_SITE_KEY is set. Without
// it the widget is simply absent and the other two layers carry the form,
// so support stays reachable rather than blocked on a missing key.

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string
      reset: (id?: string) => void
    }
  }
}

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

export function ContactForm() {
  // Signed in, the page already knows who you are. Asking a logged-in user to
  // type their own name and email into a support form is the page refusing to
  // use what it has — and it is worst for the person most likely to be here,
  // someone whose account is misbehaving.
  //
  // Prefilled rather than locked: the reply has to be able to go somewhere
  // the person can actually read. Someone locked out of the inbox on their
  // account needs to put a different address in, and that is the whole case
  // this form exists for. The route records the signed-in account separately
  // from the session, so changing this field never hides who is writing.
  const { userId, profile } = useUserRole()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  // The profile arrives after first paint, so this fills in rather than being
  // an initial value. It never overwrites something already typed.
  const [touched, setTouched] = useState(false)
  useEffect(() => {
    if (touched || !profile) return
    if (profile.full_name) setName((v) => v || profile.full_name!)
    if (profile.email) setEmail((v) => v || profile.email!)
  }, [profile, touched])

  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const mountedAt = useRef<number>(0)
  const widgetRef = useRef<HTMLDivElement>(null)
  const rendered = useRef(false)

  useEffect(() => {
    // performance.now() rather than Date.now(): this only ever measures an
    // interval, and it cannot be skewed by the clock changing underneath it.
    mountedAt.current = performance.now()
  }, [])

  useEffect(() => {
    if (!SITE_KEY || rendered.current) return
    const render = () => {
      if (rendered.current || !widgetRef.current || !window.turnstile) return
      rendered.current = true
      window.turnstile.render(widgetRef.current, { sitekey: SITE_KEY, callback: setToken })
    }
    if (window.turnstile) {
      render()
      return
    }
    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
    script.async = true
    script.onload = render
    document.head.appendChild(script)
  }, [])

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSending(true)
    const form = new FormData(e.currentTarget)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          topic: form.get("topic"),
          message: form.get("message"),
          website: form.get("website"),
          elapsedMs: performance.now() - mountedAt.current,
          turnstileToken: token,
        }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || "Could not send that.")
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send that.")
      if (SITE_KEY && window.turnstile) {
        window.turnstile.reset()
        setToken(null)
      }
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6">
        <p className="font-semibold text-foreground">Message sent.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          We read every one. You will get a reply at {email || "the address you gave"}, usually
          within a day.
        </p>
      </div>
    )
  }

  const field =
    "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-foreground/15"

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {/* The lead-in lives here rather than on the page so it can tell the
          truth in both states. "No account needed" is right for a signed-out
          visitor and odd for someone who is signed in and looking at their
          own address in the field below. */}
      <p className="text-xs text-muted-foreground">
        {userId
          ? "We will reply to the address below. Change it if you cannot read that inbox."
          : "No account needed. Tell us what happened and we will come back to you by email."}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Your name</span>
          <input
            name="name"
            autoComplete="name"
            className={field}
            value={name}
            onChange={(e) => {
              setTouched(true)
              setName(e.target.value)
            }}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Your email</span>
          <input
            name="email"
            type="email"
            required
            autoComplete="email"
            className={field}
            value={email}
            onChange={(e) => {
              setTouched(true)
              setEmail(e.target.value)
            }}
          />
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-muted-foreground">What is this about?</span>
        <select name="topic" defaultValue="General" className={field}>
          <option>General</option>
          <option>Booth rent or payments</option>
          <option>My account</option>
          <option>Reporting something</option>
          <option>Something is broken</option>
        </select>
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-muted-foreground">Message</span>
        <textarea name="message" required rows={6} maxLength={4000} className={field} />
      </label>

      {/* Honeypot. Hidden from people and from screen readers; bots fill it. */}
      <div aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      {SITE_KEY && <div ref={widgetRef} />}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={sending}
        className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-80 disabled:opacity-50"
      >
        {sending && <Loader2 className="h-4 w-4 animate-spin" />}
        Send message
      </button>
    </form>
  )
}
